import React from 'react'

interface Props {
  // What to show instead of the children once one of them has thrown. retry
  // clears the error and renders the children again.
  fallback: (retry: () => void, error: unknown) => React.ReactNode
  // A change of resetKey (another tab, another client) clears the error, so
  // moving on from a broken screen shows the next one normally.
  resetKey?: unknown
  onError?: (error: unknown) => void
  children: React.ReactNode
}

interface State {
  error: unknown
  failed: boolean
  resetKey: unknown
}

// ErrorBoundary keeps one failing part of the app (a screen whose code didn't
// load after a deploy, a sheet that hit a bug) from blanking everything else.
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, failed: false, resetKey: this.props.resetKey }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error, failed: true }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, failed: false, resetKey: props.resetKey }
    }
    return null
  }

  componentDidCatch(error: unknown): void {
    this.props.onError?.(error)
  }

  retry = () => this.setState({ error: null, failed: false })

  render() {
    if (this.state.failed) return this.props.fallback(this.retry, this.state.error)
    return this.props.children
  }
}

// isChunkLoadError: the error React.lazy reports when a screen's code file
// couldn't be fetched (offline, or renamed by a deploy since this page loaded).
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return /dynamically imported module|Importing a module script failed|error loading dynamically|Failed to fetch|ChunkLoadError|Unable to preload/i.test(message)
}

// reloadOnce reloads the page after a failed code download, but only once in
// a short while, so a file that really is missing can't loop the page.
const RELOAD_KEY = 'harmovi.reloadedForUpdate'
const RELOAD_WINDOW_MS = 60 * 1000

export function shouldReloadForUpdate(storage: Pick<Storage, 'getItem' | 'setItem'> | null, now = Date.now()): boolean {
  if (!storage) return false
  try {
    const saved = storage.getItem(RELOAD_KEY)
    const last = saved === null ? NaN : Number(saved)
    if (Number.isFinite(last) && now - last < RELOAD_WINDOW_MS) return false
    storage.setItem(RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

// AppError is the calm full-screen fallback: what happened, and one way on.
export function AppError({ onReload }: { onReload: () => void }) {
  return (
    <div className="app-error" role="alert">
      <h1 className="app-error__title">Something went wrong</h1>
      <p className="app-error__text">Reload to carry on. Anything you saved is safe.</p>
      <button type="button" className="btn btn-primary" onClick={onReload}>
        Reload
      </button>
    </div>
  )
}

// SectionError is shown inside a Settings section (or a screen) whose code
// didn't load; Retry tries again without leaving.
export function SectionError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="settings-status" role="alert">
      <p>Couldn’t load this section. Check your connection and try again.</p>
      <button type="button" className="btn btn-secondary btn-compact" onClick={onRetry}>
        Retry
      </button>
    </div>
  )
}
